import { useState } from 'react';
import { format, parseISO } from 'date-fns';
import { calendarApi, SearchResult, SearchSummary, ApiRequestError } from '@/lib/api';

interface DateRangeSearchProps {
  onNavigateToDate?: (date: string) => void;
}

export default function DateRangeSearch({ onNavigateToDate }: DateRangeSearchProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [filter, setFilter] = useState<'all' | 'available' | 'mine'>('all');
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<SearchResult[] | null>(null);
  const [summary, setSummary] = useState<SearchSummary | null>(null);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setResults(null);
    setSummary(null);

    if (!startDate || !endDate) {
      setError('Please select both start and end dates');
      return;
    }

    if (startDate > endDate) {
      setError('Start date must be before end date');
      return;
    }

    setIsSearching(true);
    try {
      const response = await calendarApi.search(startDate, endDate, filter);
      setResults(response.results);
      setSummary(response.summary);
    } catch (err) {
      if (err instanceof ApiRequestError) {
        setError(err.message);
      } else {
        setError('Failed to search. Please try again.');
      }
    } finally {
      setIsSearching(false);
    }
  };

  const handleResultClick = (date: string) => {
    if (onNavigateToDate) {
      onNavigateToDate(date);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'available':
        return 'bg-green-100 text-green-800';
      case 'approved':
        return 'bg-blue-100 text-blue-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'available':
        return 'Available';
      case 'approved':
        return 'Booked';
      case 'pending':
        return 'Pending';
      default:
        return status;
    }
  };

  return (
    <div className="card">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-4 text-left"
      >
        <div className="flex items-center gap-2">
          <svg
            className="w-5 h-5 text-gray-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
          <span className="font-medium text-gray-900">Search Availability</span>
        </div>
        <svg
          className={`w-5 h-5 text-gray-500 transition-transform ${
            isExpanded ? 'rotate-180' : ''
          }`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {isExpanded && (
        <div className="px-4 pb-4 border-t border-gray-100">
          <form onSubmit={handleSearch} className="mt-4 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label htmlFor="startDate" className="label">
                  Start Date
                </label>
                <input
                  type="date"
                  id="startDate"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="input"
                />
              </div>
              <div>
                <label htmlFor="endDate" className="label">
                  End Date
                </label>
                <input
                  type="date"
                  id="endDate"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="input"
                />
              </div>
              <div>
                <label htmlFor="filter" className="label">
                  Show
                </label>
                <select
                  id="filter"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value as typeof filter)}
                  className="input"
                >
                  <option value="all">All Dates</option>
                  <option value="available">Available Only</option>
                  <option value="mine">My Bookings Only</option>
                </select>
              </div>
            </div>

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={isSearching}
                className="btn-primary"
              >
                {isSearching ? (
                  <span className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Searching...
                  </span>
                ) : (
                  'Search'
                )}
              </button>
            </div>
          </form>

          {error && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
              {error}
            </div>
          )}

          {summary && (
            <div className="mt-4 grid grid-cols-4 gap-2 text-center text-sm">
              <div className="bg-gray-50 rounded-md p-2">
                <div className="font-semibold text-gray-900">{summary.totalDays}</div>
                <div className="text-gray-500">Total Days</div>
              </div>
              <div className="bg-green-50 rounded-md p-2">
                <div className="font-semibold text-green-700">{summary.availableDays}</div>
                <div className="text-green-600">Available</div>
              </div>
              <div className="bg-blue-50 rounded-md p-2">
                <div className="font-semibold text-blue-700">{summary.approvedDays}</div>
                <div className="text-blue-600">Booked</div>
              </div>
              <div className="bg-yellow-50 rounded-md p-2">
                <div className="font-semibold text-yellow-700">{summary.pendingDays}</div>
                <div className="text-yellow-600">Pending</div>
              </div>
            </div>
          )}

          {results && results.length > 0 && (
            <div className="mt-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">
                Results ({results.length} days)
              </h4>
              <div className="max-h-64 overflow-y-auto space-y-1">
                {results.map((result) => (
                  <button
                    key={result.date}
                    onClick={() => handleResultClick(result.date)}
                    className="w-full flex items-center justify-between p-2 rounded-md hover:bg-gray-50 text-left transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-medium text-gray-900">
                        {format(parseISO(result.date), 'EEE, MMM d, yyyy')}
                      </span>
                      {result.booking && (
                        <span
                          className="text-xs px-2 py-0.5 rounded"
                          style={{ backgroundColor: result.booking.user_color + '20', color: result.booking.user_color }}
                        >
                          {result.booking.user_name}
                        </span>
                      )}
                    </div>
                    <span
                      className={`text-xs px-2 py-1 rounded-full ${getStatusColor(result.status)}`}
                    >
                      {getStatusLabel(result.status)}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {results && results.length === 0 && (
            <div className="mt-4 text-center py-8 text-gray-500">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <p className="mt-2 text-sm">No results match your search criteria</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
